import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles, FileDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  GradientMesh,
  TiltCard,
  MockChrome,
  CountUp,
  Reveal,
  EASE_STANDARD,
  usePrefersReducedMotion,
} from '@/components/landing/v2';

/* ------------------------------------------------------------------ *
 * RotatingHeadline — AnimatePresence y-fade through the statutory     *
 * terms that prove "国内 deep AND 海外 deep". Under reduced-motion it  *
 * collapses to the static first word (no interval, no loop).          *
 * ------------------------------------------------------------------ */
function RotatingHeadline({ words, reduced }: { words: string[]; reduced: boolean }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (reduced) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % words.length), 3000);
    return () => clearInterval(id);
  }, [words.length, reduced]);

  if (reduced) {
    return <span className="gradient-text">{words[0]}</span>;
  }

  return (
    <span className="relative inline-grid align-baseline">
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={idx}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.32, ease: EASE_STANDARD }}
          className="col-start-1 row-start-1 gradient-text"
        >
          {words[idx]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Product theatre — three auto-cycling tabs. The cycle is the only    *
 * decorative loop here; it is gated and falls to the static first tab *
 * under reduced-motion. Tabs remain clickable in every case.          *
 * ------------------------------------------------------------------ */
type Tab = 0 | 1 | 2;

function HeroProductMock({ reduced }: { reduced: boolean }) {
  const { i18n, t } = useTranslation();
  const isJa = (i18n.language || 'en').startsWith('ja');
  const [tab, setTab] = useState<Tab>(0);

  useEffect(() => {
    if (reduced) return;
    const id = setInterval(() => setTab((n) => ((n + 1) % 3) as Tab), 5200);
    return () => clearInterval(id);
  }, [reduced]);

  const tabs = [
    t('hero.mock_tab_employee'),
    t('hero.mock_tab_headcount'),
    t('hero.mock_tab_ai'),
  ];

  return (
    <div className="relative mx-auto w-full max-w-md">
      {/* Soft halo — pure CSS, off the JS thread */}
      <div
        aria-hidden
        className="absolute -inset-8 -z-10 rounded-[4rem] bg-gradient-to-br from-indigo-200/55 via-fuchsia-200/35 to-sky-200/35 dark:from-indigo-500/20 dark:via-fuchsia-500/15 dark:to-sky-500/15 blur-3xl"
      />

      <TiltCard className="!p-0 overflow-hidden" glow>
        <MockChrome url="keenez.com" bodyClassName="p-0">
          {/* Tab pills */}
          <div
            role="tablist"
            aria-label={t('hero.mock_tablist_label', 'Product preview')}
            className="flex items-center gap-1.5 border-b border-border/40 px-4 py-2 bg-background/40"
          >
            {tabs.map((label, i) => (
              <button
                key={label}
                role="tab"
                type="button"
                aria-selected={tab === i}
                onClick={() => setTab(i as Tab)}
                className={`text-[11px] font-medium rounded-full px-3 py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  tab === i
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground/80'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Slide body */}
          <div className="relative h-[420px] p-5">
            <AnimatePresence mode="wait">
              {tab === 0 && <EmployeePanel key="emp" isJa={isJa} reduced={reduced} />}
              {tab === 1 && <HeadcountPanel key="hc" reduced={reduced} />}
              {tab === 2 && <BorderBuddyPanel key="ai" isJa={isJa} reduced={reduced} />}
            </AnimatePresence>
          </div>
        </MockChrome>
      </TiltCard>

      {/* Floating trust chips */}
      <div className="absolute -start-7 top-28 hidden sm:flex items-center gap-2 rounded-full glass-strong px-3 py-1.5 shadow-soft">
        <span className="relative flex h-2 w-2">
          {!reduced && (
            <motion.span
              className="absolute inline-flex h-full w-full rounded-full bg-emerald-400"
              animate={{ scale: [1, 2.2, 1], opacity: [0.7, 0, 0.7] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          )}
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
        </span>
        <span className="text-[10px] font-semibold">{t('hero.mock_chip_compliance')}</span>
      </div>
      <div className="absolute -end-6 top-48 hidden sm:flex items-center gap-2 rounded-full glass-strong px-3 py-1.5 shadow-soft">
        <span className="text-[10px]" aria-hidden>🇯🇵</span>
        <span className="num text-[10px] font-semibold">¥348,284</span>
        <span className="text-[10px] text-muted-foreground">{t('hero.mock_chip_net')}</span>
      </div>
    </div>
  );
}

const panelAnim = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.45, ease: EASE_STANDARD },
};

function EmployeePanel({ isJa, reduced }: { isJa: boolean; reduced: boolean }) {
  const { t } = useTranslation();
  return (
    <motion.div {...(reduced ? {} : panelAnim)} className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white font-semibold">
          山
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold">{isJa ? '山田 太郎' : 'Taro Yamada'}</p>
          <p className="num text-[10px] text-muted-foreground">
            JP · 正社員 · {t('hero.mock_hired')} 2024-04-01
          </p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 dark:bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          {t('status.active')}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {[
          { l: '健保', v: '協会けんぽ' },
          { l: '厚年', v: '厚生年金' },
          { l: '雇保', v: t('status.active') },
        ].map((f) => (
          <div key={f.l} className="rounded-xl bg-muted/60 px-3 py-2">
            <p className="font-jp text-[9px] uppercase tracking-wide text-muted-foreground">{f.l}</p>
            <p className="text-[11px] font-medium truncate">{f.v}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-border/60 bg-background/50 p-3">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2 font-semibold">
          {t('hero.mock_payroll_preview')}
        </p>
        <div className="space-y-1.5 text-[11px]">
          {[
            ['Gross', '¥450,000'],
            ['健保 (4.925%)', '−¥21,670'],
            ['厚年 (9.15%)', '−¥40,260'],
            ['源泉 + 住民税', '−¥37,536'],
          ].map((row) => (
            <div key={row[0]} className="flex justify-between text-muted-foreground">
              <span className="font-jp">{row[0]}</span>
              <span className="num">{row[1]}</span>
            </div>
          ))}
          <div className="mt-1 flex justify-between border-t border-border/40 pt-1.5 text-sm font-semibold">
            <span>{t('payroll.col_net')}</span>
            <span className="num text-primary">¥348,284</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function HeadcountPanel({ reduced }: { reduced: boolean }) {
  const { t } = useTranslation();
  const rows = [
    { flag: '🇯🇵', name: 'Japan', count: 12, pct: 60 },
    { flag: '🇺🇸', name: 'United States', count: 5, pct: 25 },
    { flag: '🇩🇪', name: 'Germany', count: 3, pct: 15 },
  ];
  return (
    <motion.div {...(reduced ? {} : panelAnim)} className="space-y-4">
      <div className="flex items-baseline justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('appHome.headcountByCountry')}
        </p>
        <p className="num text-2xl font-bold gradient-text">20</p>
      </div>

      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.name} className="flex items-center gap-3">
            <span className="text-base w-6" aria-hidden>{r.flag}</span>
            <span className="text-xs text-foreground/80 flex-1 truncate">{r.name}</span>
            <div className="w-32 h-1.5 bg-muted rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 origin-start"
                initial={reduced ? false : { scaleX: 0 }}
                animate={{ scaleX: 1 }}
                style={{ width: `${r.pct}%` }}
                transition={{ duration: 0.7, ease: EASE_STANDARD }}
              />
            </div>
            <span className="num text-xs font-semibold w-5 text-end">{r.count}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2 pt-2">
        {[
          { l: t('payroll.stat_gross'), v: '$87,400' },
          { l: t('payroll.stat_net'), v: '$62,118' },
          { l: t('payroll.stat_cashOutlay'), v: '$102k' },
        ].map((s) => (
          <div key={s.l} className="rounded-xl bg-muted/60 px-3 py-2 text-center">
            <p className="text-[9px] uppercase tracking-wide text-muted-foreground">{s.l}</p>
            <p className="num text-xs font-semibold mt-0.5">{s.v}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-border/60 bg-background/50 p-3">
        <p className="text-[10px] text-muted-foreground italic">
          {t('hero.mock_cross_border_blurb')}
        </p>
      </div>
    </motion.div>
  );
}

function BorderBuddyPanel({ isJa, reduced }: { isJa: boolean; reduced: boolean }) {
  const { t } = useTranslation();
  return (
    <motion.div {...(reduced ? {} : panelAnim)} className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white text-sm">
          ✦
        </span>
        <p className="text-xs font-semibold">Border Buddy</p>
        <span className="num ms-auto text-[9px] text-muted-foreground">Reasoning · $0.0012</span>
      </div>

      <div className="rounded-2xl bg-muted/60 px-3 py-2 text-[11px]">
        <p className="font-medium">
          {isJa
            ? '田中さん 2026/04→2029/04 ドイツ駐在。社会保険は?'
            : 'Tanaka-san is posted Japan → Germany 2026/04 → 2029/04. Which social insurance?'}
        </p>
      </div>

      <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white px-3 py-3 text-[11px] leading-relaxed shadow-glow">
        <p>
          {isJa
            ? '日独社会保障協定 第7条により、適用証明書(Certificate of Coverage)を取得すれば、最大5年間ドイツの社会保険を免除されます。'
            : 'Under the JP–DE Social Security Agreement (Art. 7), a Certificate of Coverage exempts up to 5 years of German social-insurance contributions.'}
        </p>
        <p className="mt-1.5 text-[9px] opacity-80">{t('demo.borderBuddySource')}</p>
      </div>

      <div className="flex items-center gap-2 text-[9px] text-muted-foreground">
        <span className="num rounded-md bg-muted px-2 py-0.5">JP-DE Art. 7</span>
        <span className="rounded-md bg-muted px-2 py-0.5">{t('hero.mock_5_year_exemption')}</span>
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ *
 * HeroSection                                                         *
 * ------------------------------------------------------------------ */
export function Hero() {
  const { t } = useTranslation();
  const reduced = usePrefersReducedMotion();

  const rotating = [
    t('hero.rotate_jp'),
    t('hero.rotate_us'),
    t('hero.rotate_uk'),
    t('hero.rotate_de'),
    t('hero.rotate_cross_border'),
  ];

  const stats = [
    { to: 4, suffix: '', label: t('hero.stat_payrolls') },
    { to: 14, suffix: '', label: t('hero.stat_languages') },
    { to: 30, suffix: 'm', label: t('hero.stat_time_to_run') },
  ];

  return (
    <section className="relative isolate overflow-hidden pt-24 pb-20 md:pt-32 md:pb-28">
      <GradientMesh blobs grid vignette />

      <div className="container relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* ---- Copy column ---- */}
          <div className="lg:col-span-7 text-center lg:text-start">
            {/* Eyebrow pill with pulsing emerald dot */}
            <Reveal delay={0}>
              <span className="inline-flex items-center gap-2 rounded-full border border-indigo-200/80 dark:border-indigo-800/50 glass-strong px-4 py-1.5 text-xs font-semibold text-indigo-700 dark:text-indigo-300 mb-6 shadow-glow">
                <span className="relative flex h-2 w-2">
                  {!reduced && (
                    <motion.span
                      className="absolute inline-flex h-full w-full rounded-full bg-emerald-400"
                      animate={{ scale: [1, 2.2, 1], opacity: [0.75, 0, 0.75] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  )}
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                {t('hero.eyebrow')}
              </span>
            </Reveal>

            {/* Headline */}
            <Reveal delay={0.05}>
              <h1 className="display serif-jp text-[2.5rem] sm:text-5xl md:text-6xl lg:text-[4.2rem] font-semibold tracking-tight leading-[1.06] text-balance">
                {t('hero.headline_part1')}{' '}
                <RotatingHeadline words={rotating} reduced={reduced} />
                <Sparkles
                  aria-hidden
                  className="inline-block ms-1 md:ms-2 h-6 w-6 md:h-7 md:w-7 align-middle text-fuchsia-500 fill-fuchsia-500/30"
                />
                <br className="hidden sm:block" />
                <span className="text-foreground">{t('hero.headline_part2')}</span>
              </h1>
            </Reveal>

            {/* Sub */}
            <Reveal delay={0.16} as="p">
              <span className="mt-6 block max-w-2xl mx-auto lg:mx-0 font-jp text-base md:text-lg text-muted-foreground leading-[1.8] text-pretty">
                {t('hero.sub')}
              </span>
            </Reveal>

            {/* CTA row */}
            <Reveal delay={0.28}>
              <div className="mt-8 flex flex-col sm:flex-row items-center gap-3 justify-center lg:justify-start">
                <Link
                  to="/signup"
                  className="gradient-primary inline-flex h-12 w-full sm:w-auto items-center justify-center gap-2 px-7 rounded-xl text-sm font-semibold text-white shadow-glow hover:shadow-glow-pink hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  {t('hero.primary_cta')}
                  <ArrowRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
                </Link>
                <Link
                  to="/demo"
                  className="inline-flex h-12 w-full sm:w-auto items-center justify-center gap-2 px-7 rounded-xl border border-border glass text-sm font-semibold hover:bg-background/80 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  <FileDown className="h-4 w-4" aria-hidden />
                  {t('hero.secondary_cta')}
                </Link>
              </div>
            </Reveal>

            {/* Free note */}
            <Reveal delay={0.4} as="p">
              <span className="mt-4 block text-xs text-muted-foreground">{t('hero.free_note')}</span>
            </Reveal>

            {/* Stat strip — count-up on mount */}
            <Reveal delay={0.5}>
              <dl className="mt-10 grid grid-cols-3 gap-4 max-w-md mx-auto lg:mx-0">
                {stats.map((s) => (
                  <div key={s.label} className="text-center lg:text-start">
                    <dt className="sr-only">{s.label}</dt>
                    <dd>
                      <CountUp
                        to={s.to}
                        suffix={s.suffix}
                        as="p"
                        className="num text-3xl md:text-4xl font-bold gradient-text"
                      />
                      <p className="font-jp text-[11px] uppercase tracking-wide text-muted-foreground mt-1">
                        {s.label}
                      </p>
                    </dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          </div>

          {/* ---- Product theatre column ---- */}
          <Reveal delay={0.2} className="lg:col-span-5">
            <HeroProductMock reduced={reduced} />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
